import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useSubscriptionCheck,
  useStripeCheckoutReturn,
  formatTrialDetail,
  type CheckoutPlanType,
  type CheckoutPlanContent,
  CHECKOUT_PLAN_CONTENT,
} from '@/features/advisor-onboarding';
import { useStripeCheckoutSession } from '@client/hooks/useStripeCheckoutSession';

import { selectStripeSubscribeSessionState } from '@client/model/selectors';
import { resetStripeSubscribeSession } from '@client/model/stripeSubscribeSessionModel';

type ViewState = 'plan-selection' | 'checkout';
type PlanType = 'trial' | 'monthly' | 'annual';

const VALID_PLAN_TYPES: CheckoutPlanType[] = ['free', 'monthly', 'annual'];

function isValidPlanType(value: string | null): value is CheckoutPlanType {
  return value !== null && VALID_PLAN_TYPES.includes(value as CheckoutPlanType);
}

interface UseStartTrialStepOptions {
  advisorId: string;
}

interface UseStartTrialStepResult {
  viewState: ViewState;
  selectedPlanType: CheckoutPlanType;
  isPending: boolean;
  pendingPlanType: PlanType | null;
  isCheckoutLoading: boolean;
  clientSecret: string | null;
  hasActiveSubscription: boolean;
  hasPendingInvitation: boolean;
  isInCheckoutFlow: boolean;
  isFetching: boolean;
  checkoutContent: CheckoutPlanContent;
  trialDetailFormatted: string | null;
  handleSelectPlan: (planType: PlanType) => void;
  handleBackToPlans: () => void;
}

export function useStartTrialStep({
  advisorId,
}: UseStartTrialStepOptions): UseStartTrialStepResult {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get('view');
  const planParam = searchParams.get('plan');

  const viewState: ViewState = viewParam === 'checkout' ? 'checkout' : 'plan-selection';
  const selectedPlanType: CheckoutPlanType = isValidPlanType(planParam) ? planParam : 'free';

  const { createCheckoutSession, isPending, pendingPlanType } = useStripeCheckoutSession({
    advisorId,
    skipNavigation: true,
  });

  const { clientSecret, isLoading: isCheckoutLoading } = useSelector(
    selectStripeSubscribeSessionState,
  );

  const { hasActiveSubscription, hasPendingInvitation, isInCheckoutFlow, isFetching } =
    useSubscriptionCheck({ advisorId });

  useStripeCheckoutReturn();

  const returnPath = `/advisors/${advisorId}/onboarding/activate-seat`;

  const handleSelectPlan = useCallback(
    (planType: PlanType) => {
      const checkoutPlanType: CheckoutPlanType = planType === 'trial' ? 'free' : planType;
      setSearchParams({ view: 'checkout', plan: checkoutPlanType });
      createCheckoutSession({ planType, returnPath });
    },
    [createCheckoutSession, returnPath, setSearchParams],
  );

  const handleBackToPlans = useCallback(() => {
    dispatch(resetStripeSubscribeSession());
    setSearchParams({}, { replace: true });
  }, [dispatch, setSearchParams]);

  // Re-create checkout session if we're in checkout view on mount (e.g., after refresh)
  useEffect(() => {
    if (viewState === 'checkout' && !clientSecret && !isCheckoutLoading && !isPending) {
      const planType: PlanType = selectedPlanType === 'free' ? 'trial' : selectedPlanType;
      createCheckoutSession({ planType, returnPath });
    }
  }, []);

  // Scroll to top when transitioning to checkout view
  useEffect(() => {
    if (viewState === 'checkout') {
      document.querySelector('body')?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  }, [viewState]);

  const checkoutContent = CHECKOUT_PLAN_CONTENT[selectedPlanType] ?? CHECKOUT_PLAN_CONTENT.free;
  const trialDetailFormatted = checkoutContent.hasTrial
    ? formatTrialDetail(checkoutContent.trialDetail, checkoutContent.trialDays)
    : null;

  // Combine loading states: show loading if mutation is pending OR Redux says it's loading
  // This prevents the error message from flashing while transitioning to checkout
  const isCheckoutSessionLoading = isPending || isCheckoutLoading;

  return {
    viewState,
    selectedPlanType,
    isPending,
    pendingPlanType,
    isCheckoutLoading: isCheckoutSessionLoading,
    clientSecret: clientSecret ?? null,
    hasActiveSubscription,
    hasPendingInvitation,
    isInCheckoutFlow,
    isFetching,
    checkoutContent,
    trialDetailFormatted,
    handleSelectPlan,
    handleBackToPlans,
  };
}
